import React, { useState } from 'react';
import Modal from './Modal';
import { Download, Calendar, User, Factory, Clock, BarChart2 } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API_BASE = 'https://mukadampro.shirpur.store';

const PdfExportModal = ({ isOpen, onClose, workers, looms, handleVoiceClick, speakHinglish }) => {
  const getToday = () => new Date().toISOString().split("T")[0];
  const getFirstDayOfMonth = () => { const date = new Date(); date.setDate(1); return date.toISOString().split('T')[0]; };

  const [reportParams, setReportParams] = useState({ 
      fromDate: getFirstDayOfMonth(), 
      toDate: getToday(), 
      workerId: 'all', 
      shift: 'all',
      frequency: 'daily',
      reportType: 'detailed' 
  });
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadPdf = async (e) => {
      if (e) e.preventDefault();
      if (speakHinglish) speakHinglish("स्मार्ट रिपोर्ट जनरेट हो रही है, कृपया प्रतीक्षा करें।");
      setIsDownloading(true);

      try {
          const profileRes = await fetch(`${API_BASE}/get_owner_profile.php`);
          const profileJson = await profileRes.json();
          const pData = profileJson.status === 'success' ? profileJson.data : {};

          const loomRes = await fetch(`${API_BASE}/manage_looms.php`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({action: 'get'})
          });
          const loomJson = await loomRes.json();
          let totalActiveLooms = pData.total_looms || "0";
          if (loomJson.status === 'success' && loomJson.active_looms) {
              totalActiveLooms = loomJson.active_looms.split(',').filter(Boolean).length.toString();
          }

          const TEXTILE_INFO = {
              name: pData.textile_name ? pData.textile_name.toUpperCase() : "MUKADAMPRO TEXTILES",
              owner: pData.owner_name ? pData.owner_name.toUpperCase() : "OWNER NAME",
              mobile: pData.mobile || "-",
              email: pData.email || "-",
              gst: pData.gstin || "-",
              address: pData.address || "-",
              totalLooms: totalActiveLooms
          };

          let filteredWorkers = workers || [];
          if (reportParams.workerId !== 'all') {
              filteredWorkers = filteredWorkers.filter(w => String(w.id) === String(reportParams.workerId));
          }
          if (reportParams.shift !== 'all') {
              filteredWorkers = filteredWorkers.filter(w => (w.shift || '').toLowerCase() === reportParams.shift.toLowerCase());
          }

          const rangeRes = await fetch(`${API_BASE}/search_api.php`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ type: 'date', start_date: reportParams.fromDate, end_date: reportParams.toDate })
          });
          const rangeJson = await rangeRes.json();
          const globalData = rangeJson.status === 'success' ? rangeJson.data : [];
          
          const getRowDate = (item) => item.date || item.entry_date || (item.created_at ? item.created_at.split(' ')[0] : '');
          const uniqueDates = [...new Set(globalData.map(getRowDate).filter(Boolean))].sort();

          const fetchPromises = filteredWorkers.map(async (w) => {
              const res = await fetch(`${API_BASE}/search_api.php`, {
                  method: 'POST', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ type: 'worker', start_date: reportParams.fromDate, end_date: reportParams.toDate, id: w.id })
              });
              const json = await res.json();
              const workerLooms = {}; let workerTotal = 0; let workerTotalEarn = 0;
              if (json.status === 'success' && json.data) {
                  json.data.forEach(r => {
                      if (reportParams.shift !== 'all' && (r.shift || '').toLowerCase() !== reportParams.shift.toLowerCase()) return;
                      const lId = r.loom_id || 'NA';
                      
                      const meters = parseFloat(r.meters || r.total || r.production_meters || 0);
                      const earn = parseFloat(r.earning || r.amount || r.earnings || r.total_earning || 0);
                      const rDate = getRowDate(r);
                      
                      if (!workerLooms[lId]) { workerLooms[lId] = {}; uniqueDates.forEach(d => workerLooms[lId][d] = 0); }
                      if (rDate && workerLooms[lId][rDate] !== undefined) workerLooms[lId][rDate] += meters;
                      workerTotal += meters;
                      workerTotalEarn += earn;
                  });
              }
              return { worker: w, looms: workerLooms, total: workerTotal, totalEarn: workerTotalEarn };
          });

          const workersDataResults = await Promise.all(fetchPromises);
          
          const summaryTableBody = [];
          let overallTotalMeters = 0;
          let overallTotalEarnings = 0;

          workersDataResults.forEach((res) => {
              const w = res.worker;
              const totalM = res.total || 0;
              
              // 🌟 FINAL FIX: Added all possible database keys for rate 🌟
              let rate = parseFloat(w.rate_per_meter || w.ratePerMeter || w.rate || w.salary || w.rate_per_m || 0);
              
              // Fallback: Agar search_api earning return karta hai
              if (rate === 0 && res.totalEarn > 0 && totalM > 0) {
                  rate = res.totalEarn / totalM; 
              }
              res.calculatedRate = rate; 
              
              const totalE = totalM * rate;
              
              const totalLooms = Object.keys(res.looms).length;
              const shift = w.shift ? w.shift.toUpperCase() : '-';

              overallTotalMeters += totalM;
              overallTotalEarnings += totalE;

              summaryTableBody.push([
                  w.name.toUpperCase(),
                  shift,
                  totalLooms,
                  `Rs. ${rate.toFixed(2)}`,
                  `${totalM.toFixed(1)} Meter`,
                  `Rs. ${totalE.toFixed(0)}`
              ]);
          });

          summaryTableBody.push([
              { content: 'GRAND TOTAL (ALL WORKERS)', colSpan: 4, styles: { fontStyle: 'bold', halign: 'right', fillColor: [241, 245, 249], textColor: [15, 23, 42] } },
              { content: `${overallTotalMeters.toFixed(1)} Meter`, styles: { fontStyle: 'bold', fillColor: [241, 245, 249], textColor: [15, 23, 42] } },
              { content: `Rs. ${overallTotalEarnings.toFixed(0)}`, styles: { fontStyle: 'bold', fillColor: [241, 245, 249], textColor: [15, 23, 42] } }
          ]);

          const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
          const centerX = 297 / 2;

          const drawWatermark = () => {
              doc.setTextColor(240, 245, 250); 
              doc.setFontSize(45);
              doc.setFont("helvetica", "bold");
              doc.text(TEXTILE_INFO.name, centerX, 110, { align: "center", angle: 35 });
          };

          drawWatermark();

          const originalAddPage = doc.addPage.bind(doc);
          doc.addPage = function(...args) {
              originalAddPage(...args);
              drawWatermark(); 
              return this;     
          };

          doc.setFont("helvetica", "bold");
          doc.setFontSize(16);
          doc.setTextColor(15, 23, 42); 
          doc.text("PRODUCTION REPORT", centerX, 15, { align: "center" });

          const downloadTime = new Date().toLocaleString('en-IN', { 
              day: 'numeric', month: 'short', year: 'numeric', 
              hour: '2-digit', minute: '2-digit', hour12: true 
          });
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);
          doc.setTextColor(100, 116, 139);
          doc.text(`Downloaded: ${downloadTime}`, 297 - 14, 15, { align: "right" });

          doc.setFontSize(20);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(51, 65, 85);
          doc.text(TEXTILE_INFO.name, centerX, 24, { align: "center" });

          doc.setFont("helvetica", "normal");
          doc.setFontSize(9);
          doc.setTextColor(100, 116, 139);

          const line1 = `Owner: ${TEXTILE_INFO.owner}   |   Total Looms: ${TEXTILE_INFO.totalLooms}   |   Mobile: ${TEXTILE_INFO.mobile}`;
          doc.text(line1, centerX, 30, { align: "center" });

          const line2 = `Email: ${TEXTILE_INFO.email}   |   GST No: ${TEXTILE_INFO.gst}`;
          doc.text(line2, centerX, 35, { align: "center" });

          const line3 = `Address: ${TEXTILE_INFO.address}`;
          doc.text(line3, centerX, 40, { align: "center" });

          doc.setDrawColor(226, 232, 240); 
          doc.setLineWidth(0.5);
          doc.line(14, 44, 297 - 14, 44);

          doc.setFont("helvetica", "bold");
          doc.setFontSize(12);
          doc.setTextColor(15, 23, 42);
          
          const dateRangeText = `Period: ${reportParams.fromDate} TO ${reportParams.toDate}`;
          let summaryTitle = "Worker Grand Summary";
          if(reportParams.workerId !== 'all') {
              const wName = workers.find(w => String(w.id) === String(reportParams.workerId))?.name || '';
              summaryTitle = `Summary: ${wName.toUpperCase()}`;
          } else if(reportParams.shift !== 'all') {
              summaryTitle = `Grand Summary (${reportParams.shift.toUpperCase()} SHIFT)`;
          }
          doc.text(summaryTitle, centerX, 50, { align: "center" }); 
          doc.setFontSize(10);
          doc.setFont("helvetica", "normal");
          doc.text(dateRangeText, centerX, 55, { align: "center" });

         autoTable(doc, {
              head: [['Worker Name', 'Shift', 'Total Looms', 'Rate/Meter', 'Total Prod.', 'Total Earning']],
              body: summaryTableBody,
              startY: 60, 
              theme: 'grid',
              headStyles: { fillColor: [15, 23, 42], textColor: 255, fontSize: 9, halign: 'center' },
              
              // NAYA: Summary table ke liye halke grey rows
              alternateRowStyles: { fillColor: [247, 247, 247] },
              styles: { fillColor: [255, 255, 255], fontSize: 8.5, halign: 'center', cellPadding: 4, textColor: [15, 23, 42] },
              
              columnStyles: { 0: { halign: 'left', fontStyle: 'bold' } },
              margin: { left: 14, right: 14 }
          });

          // ==========================================
          // SMART DATE GROUPING LOGIC (UPDATED WITH FULL DATES)
          // ==========================================
          const isDaily = reportParams.frequency === 'daily';
          const periodsMap = new Map();

          uniqueDates.forEach(d => {
              let key;
              if (isDaily) {
                  key = new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'long' });
              } else if (reportParams.frequency === 'weekly') {
                  const dateObj = new Date(d);
                  const day = dateObj.getDay();
                  const diff = dateObj.getDate() - day + (day === 0 ? -6 : 1);
                  const monday = new Date(new Date(dateObj).setDate(diff));
                  const sunday = new Date(monday);
                  sunday.setDate(sunday.getDate() + 6);
                  const format = (dt) => dt.toLocaleDateString('en-IN', { day: 'numeric', month: 'long' });
                  key = `${format(monday)} - ${format(sunday)}`;
              } else { 
                  key = new Date(d).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
              }

              if (!periodsMap.has(key)) periodsMap.set(key, []);
              periodsMap.get(key).push(d);
          });
          const periods = Array.from(periodsMap.keys());

          // ==========================================
          // PAGE 2+: DETAILED PRODUCTION (IF SELECTED)
          // ==========================================
          if (reportParams.reportType === 'detailed' && workersDataResults.length > 0) {
              const reportTitle = isDaily ? "Detailed Daily Production" : (reportParams.frequency === 'weekly' ? "Detailed Weekly Production" : "Detailed Monthly Production");

              const formatDateCustom = (dateString) => {
                  const d = new Date(dateString);
                  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
              };
              const formattedDateRange = `${formatDateCustom(reportParams.fromDate)} to ${formatDateCustom(reportParams.toDate)}`;

              workersDataResults.forEach((res) => {
                  const w = res.worker;
                  const loomsData = res.looms;
                  const rate = res.calculatedRate || 0; // ऊपर जो असली रेट कैलकुलेट किया, वही यूज़ होगा
                  const sortedLooms = Object.keys(loomsData).sort((a, b) => parseInt(a) - parseInt(b));                  
                  if (sortedLooms.length === 0) return;

                  const loomChunks = [];
                  for (let i = 0; i < sortedLooms.length; i += 12) {
                      loomChunks.push(sortedLooms.slice(i, i + 12));
                  }

                  let currentY = 25;

                  loomChunks.forEach((chunk, chunkIndex) => {

                      if (chunkIndex === 0) {
                          doc.addPage();
                          doc.setFont("helvetica", "bold");
                          doc.setFontSize(14);
                          doc.setTextColor(15, 23, 42);
                          doc.text(reportTitle, centerX, 15, { align: "center" });

                          doc.setFont("helvetica", "bold");
                          doc.setFontSize(10);
                          doc.setTextColor(15, 23, 42);
                          doc.setFillColor(241, 245, 249);
                          doc.rect(10, currentY - 5, 297 - 20, 8, 'F'); 
                          doc.text(`Worker Name: ${w.name.toUpperCase()}    |    Shift: ${w.shift ? w.shift.toUpperCase() : '-'}`, 12, currentY);
                          doc.text(formattedDateRange, 297 - 12, currentY, { align: "right" });
                          
                          currentY += 6;
                      } else {
                          currentY += 10;
                      }

                      const periodHeaderLabel = isDaily ? 'Date' : (reportParams.frequency === 'weekly' ? 'Week' : 'Month');
                      
                      const head1 = [
                          { content: periodHeaderLabel, styles: { valign: 'middle', halign: 'center', fillColor: [15, 23, 42] } }
                      ];

                      chunk.forEach(lId => {
                          head1.push({ content: `Loom\n${String(lId).padStart(2, '0')}`, styles: { halign: 'center', fillColor: [15, 23, 42] } });
                      });
                      
                      head1.push({ content: 'Total\nProd/Earn', styles: { valign: 'middle', halign: 'center', fillColor: [15, 23, 42] } });

                      const tableBody = [];

                      periods.forEach(periodKey => {
                          const rawDates = periodsMap.get(periodKey);
                          let workerTotalForPeriodProd = 0;
                          sortedLooms.forEach(lId => {
                              rawDates.forEach(d => { workerTotalForPeriodProd += (loomsData[lId][d] || 0); });
                          });
                          let workerTotalForPeriodEarn = workerTotalForPeriodProd * rate;

                          const row = [ { content: periodKey } ];
                          
                          chunk.forEach(lId => {
                              let periodLoomProd = 0;
                              rawDates.forEach(d => { periodLoomProd += (loomsData[lId][d] || 0); });
                              
                              row.push({ 
                                  content: `${periodLoomProd.toFixed(1)}\nMeter`, 
                                  // UPDATE: fontSize 8.5 किया और fontStyle 'bold' कर दिया 
                                  styles: { textColor: [15, 23, 42], valign: 'middle', fontSize: 8.5, fontStyle: 'bold' } 
                              });
                          });
                          
                         // UPDATE: 'Prod' को 'Production' किया और fontSize 9.5 कर दिया
                          const totalCellText = `Production: ${workerTotalForPeriodProd.toFixed(1)}\nMeter\nEarn: Rs.${workerTotalForPeriodEarn.toFixed(0)}`;
                          row.push({ 
                              content: totalCellText, 
                              styles: { fillColor: [241, 245, 249], textColor: [15, 23, 42], fontStyle: 'bold', halign: 'center', valign: 'middle', fontSize: 9.5, cellPadding: 2 } 
                          });

                          tableBody.push(row);
                      });

                      autoTable(doc, {
                          head: [head1], 
                          body: tableBody,
                          startY: currentY,
                          theme: 'grid',
                          rowPageBreak: 'avoid',
                          headStyles: { fontSize: 8, textColor: 255, cellPadding: 2 },
                          alternateRowStyles: { fillColor: [247, 247, 247] }, 
                          styles: { fillColor: [255, 255, 255], fontSize: 7.5, halign: 'center', cellPadding: 1.5, textColor: [15, 23, 42], valign: 'middle' },
                          columnStyles: {
                              0: { fontSize: 8.5, fontStyle: 'bold', fillColor: [241, 245, 249], textColor: [15, 23, 42], cellWidth: 24, valign: 'middle', halign: 'center' }
                          },
                          horizontalPageBreak: true, 
                          margin: { left: 10, right: 10 }, 
                          didDrawPage: function (data) {
                              currentY = data.cursor.y; 
                          }
                      });
                  }); 
              }); 
          }

          const totalPages = doc.internal.getNumberOfPages();
          for (let i = 1; i <= totalPages; i++) {
              doc.setPage(i);
              doc.setFont("helvetica", "normal");
              doc.setFontSize(9);
              doc.setTextColor(100, 116, 139);
              doc.text(`Page ${i} of ${totalPages}`, 297 - 14, 210 - 10, { align: "right" });
          }

          doc.save(`Production_Report_${reportParams.frequency}_${getToday()}.pdf`);
          onClose();
      } catch (err) { alert("Error: " + err.message); } 
      finally { setIsDownloading(false); }
  };

return (
    <Modal 
        icon={Download} 
        iconBg="linear-gradient(135deg, #DBEAFE, #93C5FD)" 
        iconColor="#2563EB" 
        isOpen={isOpen} 
        maxWidth="400px" 
        onClose={onClose} 
        title="Download Production Report"
    >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '10px' }}>
            
            <div style={{display: 'flex', flexDirection: 'column', gap: '6px'}}>
                <label style={{fontSize: '11px', fontWeight: '600', color: '#64748B', textTransform: 'uppercase', paddingLeft: '4px'}}>Select Worker</label>
                <select value={reportParams.workerId} onChange={e => setReportParams({...reportParams, workerId: e.target.value})} style={{ padding: '12px', borderRadius: '12px', border: '1px solid #e2e8f0', background: '#F8FAFC', outline: 'none', cursor: 'pointer', fontWeight: '500', color: '#0F172A', width: '100%', boxSizing: 'border-box' }}>
                    <option value="all">All Workers</option>
                    {workers && workers.map(w => (
                        <option key={w.id} value={w.id}>{w.name}</option>
                    ))}
                </select>
            </div>

            <div style={{display: 'flex', flexDirection: 'column', gap: '6px'}}>
                <label style={{fontSize: '11px', fontWeight: '600', color: '#64748B', textTransform: 'uppercase', paddingLeft: '4px'}}>Select Shift</label>
                <select value={reportParams.shift} onChange={e => setReportParams({...reportParams, shift: e.target.value})} style={{ padding: '12px', borderRadius: '12px', border: '1px solid #e2e8f0', background: '#F8FAFC', outline: 'none', cursor: 'pointer', fontWeight: '500', color: '#0F172A', width: '100%', boxSizing: 'border-box' }}>
                    <option value="all">All Shifts</option>
                    <option value="day">Day Shift</option>
                    <option value="night">Night Shift</option>
                </select>
            </div>

            <div style={{display: 'flex', flexDirection: 'column', gap: '6px'}}>
                <label style={{fontSize: '11px', fontWeight: '600', color: '#64748B', textTransform: 'uppercase', paddingLeft: '4px'}}>Report Frequency</label>
                <select value={reportParams.frequency} onChange={e => setReportParams({...reportParams, frequency: e.target.value})} style={{ padding: '12px', borderRadius: '12px', border: '1px solid #e2e8f0', background: '#F8FAFC', outline: 'none', cursor: 'pointer', fontWeight: '500', color: '#0F172A', width: '100%', boxSizing: 'border-box' }}>
                    <option value="daily">Daily Report</option>
                    <option value="weekly">Weekly Report</option>
                    <option value="monthly">Monthly Report</option>
                </select>
            </div>

            <div style={{display: 'flex', flexDirection: 'column', gap: '6px'}}>
                <label style={{fontSize: '11px', fontWeight: '600', color: '#64748B', textTransform: 'uppercase', paddingLeft: '4px'}}>Report Type</label>
                <select value={reportParams.reportType} onChange={e => setReportParams({...reportParams, reportType: e.target.value})} style={{ padding: '12px', borderRadius: '12px', border: '1px solid #e2e8f0', background: '#F8FAFC', outline: 'none', cursor: 'pointer', fontWeight: '500', color: '#0F172A', width: '100%', boxSizing: 'border-box' }}>
                    <option value="detailed">Full Detailed Report</option>
                    <option value="summary">Summary Only (Fast)</option>
                </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={{display: 'flex', flexDirection: 'column', gap: '6px'}}>
                    <label style={{fontSize: '11px', fontWeight: '600', color: '#64748B', textTransform: 'uppercase', paddingLeft: '4px'}}>From Date</label>
                    <input type="date" value={reportParams.fromDate} onChange={e => setReportParams({...reportParams, fromDate: e.target.value})} style={{ padding: '12px', borderRadius: '12px', border: '1px solid #e2e8f0', outline: 'none', background: '#F8FAFC', width: '100%', boxSizing: 'border-box' }} />
                </div>
                <div style={{display: 'flex', flexDirection: 'column', gap: '6px'}}>
                    <label style={{fontSize: '11px', fontWeight: '600', color: '#64748B', textTransform: 'uppercase', paddingLeft: '4px'}}>To Date</label>
                    <input type="date" value={reportParams.toDate} onChange={e => setReportParams({...reportParams, toDate: e.target.value})} style={{ padding: '12px', borderRadius: '12px', border: '1px solid #e2e8f0', outline: 'none', background: '#F8FAFC', width: '100%', boxSizing: 'border-box' }} />
                </div>
            </div>

            <button 
                onClick={handleDownloadPdf} 
                disabled={isDownloading} 
                style={{ 
                    width: '100%', padding: '16px', borderRadius: '14px', 
                    background: 'linear-gradient(135deg, #3B82F6, #2563EB)', 
                    color: '#ffffff', fontWeight: '600', marginTop: '6px', 
                    boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)', 
                    cursor: 'pointer', border: 'none', 
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' 
                }}
            >
                <Download color="#ffffff" size={16} /> 
                {isDownloading ? 'Processing Details...' : 'Download Production Report'}
            </button>
        </div>
    </Modal>
  );
};

export default PdfExportModal;